/**
 * The canvas panel (web), Gemini's immersive panel for documents, slides and
 * pages.
 * Docked beside the chat it is a raised card (surface-container-lowest, 16px
 * radius, 1px outline); on phones and inside dialogs it covers the viewport.
 *
 * Header, 56px: kind glyph, title, a saved glyph, then Copy link, a tonal
 * Download pill whose menu lists the files, Expand (docked only) and Close.
 * Under it a segmented row when there is more than one view (Preview / PDF),
 * then the preview itself: the sandboxed HTML page (empty sandbox, no
 * referrer, the dark copy in a dark app) or the PDF, inset 16px with 12px
 * corners. A `page` is the Preview alone, in its own design, with HTML its
 * one download. `ArtifactPanel.tsx` is the native twin with the same props.
 *
 * Share (plan 7.6) is a menu: Copy private link and Publish to the web, or,
 * once the page is live, Copy site link, Open site and Manage site; a live
 * site also shows the "Live" chip with a globe beside the title.
 *
 * While the streaming writer builds the artifact (plan 7.2), the header shows
 * a "Building" chip where the saved glyph sits, Share and Download wait for
 * the file, and the body is `ArtifactDraftPanel` (skeleton, live snapshots,
 * and the code for a page) until the stored artifact is ready.
 *
 * Editing (plan 7.3 to 7.5): Edit in the header (latest version only, never
 * while a build or an agent edit streams) swaps the preview for the editor:
 * the page in its editing frame, or the document in Tiptap; the header then
 * shows the save state and Done. A saved page has a Code tab, editable on
 * the latest version. The row under the header holds the version control
 * (Previous version, "Version 3 of 5", Next version, the list); an older
 * version shows its banner with Restore this version and Back to latest.
 *
 * An agent edit of the open file (`updatePage` or `updateDocument` in the
 * chat, user test fix 2026-09-25) keeps the file on screen, dimmed, with an
 * "Editing" chip and the edit's progress from its `artifact-draft` events;
 * the panel jumps to the latest version, and when the new version is the
 * head (the tool result or the `artifact:update` nudge reads it again) the
 * preview reloads with a new link, the Code tab reads the new source and the
 * version control counts it.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { AgentArtifactKind, ArtifactDownloadVariant } from '@tabbio/types';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { SpinnerGlyph } from '../common/SpinnerGlyph';
import { WebMenu, WebMenuItem } from '../web/WebMenu';
import { useToast } from '../../hooks/useToast';
import { useTheme } from '../../hooks/useTheme';
import { useArtifactDetail, useArtifactSignedUrl } from '../../hooks/useArtifact';
import { mintPreviewUrl } from '../../lib/artifacts-api';
import { downloadArtifactFile } from '../../lib/artifact-download';
import { copyArtifactLink } from '../../lib/artifact-links';
import {
  artifactDownloadOptions,
  artifactModelFromSummary,
  artifactPreviewPlan,
  artifactPreviewTheme,
  resolveArtifactViewerState,
  type ArtifactPanelProps,
} from './artifact-model';
import { ArtifactDraftPanel } from './ArtifactDraftPanel';
import { useArtifactDraftFor } from './artifact-draft-store';
import {
  agentEditState,
  draftFailureKey,
  isDraftTerminal,
  panelDraftState,
  type AgentEditState,
} from './artifact-draft-model';
import { useArtifactEditFor, useSettleArtifactEdits } from './artifact-edit-activity';
import { useArtifactVersion, useEditingAvailable } from '../../hooks/useArtifactVersions';
import { useVersionControl } from './versions/useVersionControl';
import { OlderVersionBanner, VersionControl } from './versions/VersionControl.web';
import { SaveStatus } from './editor/EditorChrome.web';
import { PageEditor, type EditorHandle } from './editor/PageEditor.web';
import { DocumentEditor } from './editor/DocumentEditor.web';
import { PageCodeEditor } from './editor/PageCodeEditor.web';
import type { EditorSaveState } from './editor/save-state';
import { useArtifactSite, usePublishingCapability } from '../../hooks/useSites';
import { isSiteLive, siteHost } from '../sites/publish-sheet-model';
import { openPublishSheet } from '../sites/open-publish-sheet';
import { copySiteLink, openSite } from '../sites/site-share';
import { trackArtifactEditOpened, type EditOpenedFrom } from './artifact-analytics';

const KIND_ICONS: Record<AgentArtifactKind, string> = {
  document: 'description',
  slides: 'slideshow',
  page: 'web',
  image: 'image',
  file: 'draft',
};

export function ArtifactPanel({
  artifactId,
  layout,
  expanded = false,
  onToggleExpanded,
  onClose,
  closeMode = 'close',
  initialView,
  viewRequest,
  takeFocus = true,
  versionId: versionProp,
  onVersionChange,
}: ArtifactPanelProps) {
  const { t } = useTranslation('common');
  const toast = useToast();
  const { isDark } = useTheme();
  const detail = useArtifactDetail(artifactId);
  const artifact = detail.data ? artifactModelFromSummary(detail.data) : null;
  const plan = artifact ? artifactPreviewPlan(artifact, { inlinePdf: true }) : null;
  const [view, setView] = useState<'page' | 'pdf' | 'code'>('page');
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [saveState, setSaveState] = useState<EditorSaveState>({ status: 'idle' });
  const editorRef = useRef<EditorHandle | null>(null);
  // The version on screen: the panel store's when it has one, else our own.
  const [ownVersion, setOwnVersion] = useState<string | null>(null);
  const viewingId = onVersionChange ? versionProp ?? null : ownVersion;
  const setViewingId = useCallback(
    (next: string | null) => (onVersionChange ? onVersionChange(next) : setOwnVersion(next)),
    [onVersionChange],
  );
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [busy, setBusy] = useState<ArtifactDownloadVariant | null>(null);
  const downloadRef = useRef<HTMLButtonElement | null>(null);
  const shareRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLElement | null>(null);

  // A replaced artifact starts on its page view.
  useEffect(() => setView('page'), [artifactId]);

  // The writer's live draft of this artifact, while it builds.
  const draft = useArtifactDraftFor(artifactId);
  const draftState = panelDraftState(draft, artifact?.status);
  const [buildView, setBuildView] = useState<'preview' | 'code'>(initialView === 'code' ? 'code' : 'preview');
  // A building card's Open asks for the code again on a panel already open.
  useEffect(() => {
    if (initialView === 'code' || initialView === 'preview') setBuildView(initialView);
  }, [initialView, viewRequest]);

  // An agent edit of this file (user test fix, 2026-09-25): the file stays
  // on screen, dimmed, with "Editing" (or "Checking the page") until the new
  // version is the head; then the head is read again and shown.
  const head = detail.data?.currentVersion ?? null;
  const editActivity = useArtifactEditFor(artifactId);
  useSettleArtifactEdits(artifactId, head);
  const agentEditing = agentEditState({
    draft,
    activity: editActivity && editActivity.kind !== 'image' ? editActivity : null,
    headNumber: head?.number,
  });
  // An edit that ended on a version newer than the head read: read it again.
  const endedVersion =
    draft?.mode === 'edit' && draft.phase === 'end' ? draft.versionNumber : editActivity?.versionNumber ?? null;
  const { refetch: refetchDetail } = detail;
  useEffect(() => {
    if (endedVersion !== null && (head?.number ?? 0) < endedVersion) void refetchDetail();
  }, [endedVersion, head?.number, refetchDetail]);

  // Editing (plan 7.3): never while a build or an agent edit streams, never
  // on an older version, only where this account has editing.
  const editingAvailable = useEditingAvailable();
  const streaming = Boolean(draft && !isDraftTerminal(draft)) || agentEditing !== null;
  const versionControl = useVersionControl({
    artifactId,
    viewingId,
    onViewingChange: setViewingId,
    enabled: artifact?.status === 'ready',
  });
  const onLatest = versionControl.model.isLatest;
  // An agent edit shows the latest version: a panel on an older one jumps to
  // the head (the older-version banner clears), and so does a new head.
  const agentEditingNow = agentEditing !== null;
  useEffect(() => {
    if (agentEditingNow && viewingId) setViewingId(null);
    // Only when an edit starts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentEditingNow]);
  const seenHead = useRef(head?.id ?? null);
  useEffect(() => {
    const previous = seenHead.current;
    seenHead.current = head?.id ?? null;
    if (previous && head?.id && previous !== head.id && viewingId) setViewingId(null);
    // Only when the head moves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [head?.id]);
  const editableKind = artifact?.kind === 'page' || artifact?.kind === 'document' || artifact?.kind === 'slides';
  const canEdit =
    editingAvailable && artifact?.status === 'ready' && editableKind && onLatest && draftState === 'none' && !streaming;
  const editing = mode === 'edit' && canEdit;
  // Leaving the conditions (a build starts, another version) leaves edit mode.
  useEffect(() => {
    if (mode === 'edit' && !canEdit) setMode('view');
  }, [canEdit, mode]);
  // The suggestions card's Open to review opens the editor.
  const editOpenedFrom = useRef<EditOpenedFrom>('header');
  useEffect(() => {
    if (initialView === 'edit') {
      editOpenedFrom.current = 'suggestions';
      setView('page');
      setMode('edit');
    }
  }, [initialView, viewRequest]);
  // Each time the editor opens, counted once (`artifact_panel_edit_opened`).
  const editingSeen = useRef(false);
  useEffect(() => {
    if (editing && !editingSeen.current) trackArtifactEditOpened(artifact?.kind, editOpenedFrom.current);
    editingSeen.current = editing;
  }, [artifact?.kind, editing]);

  // Focus follows the mode switch: Done after Edit, Edit after Done (plan 7.8).
  const editButtonRef = useRef<HTMLButtonElement | null>(null);
  const doneButtonRef = useRef<HTMLButtonElement | null>(null);
  const modeSeen = useRef(editing);
  useEffect(() => {
    if (modeSeen.current === editing) return;
    modeSeen.current = editing;
    const frame = window.requestAnimationFrame(() =>
      (editing ? doneButtonRef.current : editButtonRef.current)?.focus({ preventScroll: true }),
    );
    return () => window.cancelAnimationFrame(frame);
  }, [editing]);

  const enterEdit = () => {
    editOpenedFrom.current = 'header';
    setView('page');
    setSaveState({ status: 'idle' });
    setMode('edit');
  };
  const done = async () => {
    const stored = (await editorRef.current?.flush()) ?? true;
    // A save that can still be retried keeps the editor open.
    if (!stored && (saveState.status === 'failed' || saveState.status === 'limited')) return;
    setMode('view');
    setSaveState({ status: 'idle' });
  };

  // Focus moves into the panel when it opens and goes back to the opener
  // (the card's Open button) when it closes.
  // A panel the writer opened by itself leaves focus where the reader is
  // (often the composer).
  useEffect(() => {
    if (!takeFocus) return;
    const opener =
      typeof document !== 'undefined' && document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const frame = window.requestAnimationFrame(() => panelRef.current?.focus({ preventScroll: true }));
    return () => {
      window.cancelAnimationFrame(frame);
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    };
    // On mount only: the host keys the panel by artifact, so each open mounts it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const variant = plan?.primary ? (view === 'pdf' && plan.pdf ? 'pdf' : plan.primary) : null;
  // The version on screen: its stale PDF waits for the formats job (plan 3.2).
  const shownVersionId = viewingId ?? detail.data?.currentVersion?.id ?? null;
  const [pdfWaiting, setPdfWaiting] = useState(false);
  const shownVersion = useArtifactVersion(artifactId, shownVersionId, {
    enabled: artifact?.status === 'ready' && artifact.kind === 'document',
    refetchInterval: pdfWaiting ? 3_000 : false,
  });
  const stale = Boolean(shownVersion.data?.formatsStale?.includes('pdf'));
  const waitingForPdf = view === 'pdf' && artifact?.kind === 'document' && (stale || !plan?.pdf);
  useEffect(() => setPdfWaiting(waitingForPdf), [waitingForPdf]);
  // Opening the PDF of a saved document asks for its render now (the server
  // answers 409 while it runs), then waits for it.
  useEffect(() => {
    if (!waitingForPdf) return;
    void mintPreviewUrl(artifactId, 'pdf', 'light', viewingId).catch(() => undefined);
    const timer = setInterval(() => void detail.refetch(), 3_000);
    return () => clearInterval(timer);
    // The detail's refetch is stable; the ids decide.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waitingForPdf, artifactId, viewingId]);
  const preview = useArtifactSignedUrl(
    artifactId,
    variant,
    artifact?.status === 'ready' && !waitingForPdf && !(editing && view === 'page') && view !== 'code',
    artifactPreviewTheme(variant, isDark, artifact?.kind),
    {
      versionId: viewingId,
      revision: `${detail.data?.currentVersion?.id ?? ''}:${detail.data?.updatedAt ?? ''}`,
    },
  );

  const state = resolveArtifactViewerState({
    artifact,
    loading: detail.isLoading,
    error: detail.error,
    plan,
    previewExpired: preview.expired,
    previewError: preview.error,
  });

  const title = artifact?.title || draft?.title || t('chat.artifacts.viewer.title');
  const downloads = artifact ? artifactDownloadOptions(artifact) : [];
  const docked = layout === 'docked';
  const closeLabel =
    closeMode === 'backToChat' ? t('chat.artifacts.actions.backToChat') : t('chat.artifacts.actions.close');

  const download = async (next: ArtifactDownloadVariant) => {
    if (busy) return;
    setBusy(next);
    try {
      await downloadArtifactFile({ artifactId, variant: next, title });
    } catch {
      toast.error(t('chat.artifacts.download.failed'));
    } finally {
      setBusy(null);
    }
  };

  const copyLink = async () => {
    if (await copyArtifactLink(artifactId)) toast.success(t('chat.artifacts.actions.linkCopied'));
    else toast.error(t('chat.artifacts.actions.copyFailed'));
  };

  // Publishing (plan 7.6): the site of this file, when publishing is on.
  const publishing = usePublishingCapability();
  const publishable = artifact?.status === 'ready' && artifact.kind !== 'file';
  const siteQuery = useArtifactSite(artifactId, { enabled: publishable && Boolean(publishing?.available) });
  const site = siteQuery.data ?? null;
  const live = isSiteLive(site);
  const liveHost = live ? siteHost(site?.url) : '';
  const openPublish = (intent: 'publish' | 'manage') =>
    openPublishSheet({ artifactId, intent, title: artifact?.title ?? null });
  const copySite = async () => {
    if (site?.url && (await copySiteLink(site.url))) toast.success(t('chat.artifacts.actions.linkCopied'));
    else toast.error(t('chat.artifacts.actions.copyFailed'));
  };

  // A document keeps its PDF tab while a save left the PDF waiting for its render.
  const pdfTab = Boolean(
    (state === 'ready' && plan?.pdf) || (artifact?.kind === 'document' && (stale || artifact.formats.includes('pdf'))),
  );
  const tabs: ('page' | 'pdf' | 'code')[] =
    artifact?.kind === 'page' ? ['page', 'code'] : pdfTab ? ['page', 'pdf'] : ['page'];
  // On a phone the panel covers the chat: after Ask Tabbio it gets out of the way.
  const askedCloses = layout === 'fullscreen' ? onClose : undefined;

  let body: ReactNode;
  switch (state) {
    case 'loading':
      body = <PanelMessage spinner title={t('chat.artifacts.viewer.loading')} />;
      break;
    case 'pending':
      body = <PanelMessage spinner title={t('chat.artifacts.viewer.pending')} />;
      break;
    case 'failed':
      body = <PanelMessage icon="error" title={t('chat.artifacts.failed')} />;
      break;
    case 'unsupported':
      body = <PanelMessage icon="draft" title={t('chat.artifacts.viewer.unsupported')} />;
      break;
    case 'offline':
      body = (
        <PanelMessage
          icon="wifi_off"
          title={t('chat.artifacts.viewer.offline')}
          action={{ label: t('chat.artifacts.actions.retry'), onPress: () => void detail.refetch() }}
        />
      );
      break;
    case 'expired':
      body = (
        <PanelMessage
          icon="schedule"
          title={t('chat.artifacts.viewer.expired')}
          action={{ label: t('chat.artifacts.actions.retry'), onPress: preview.retry }}
        />
      );
      break;
    case 'not-found':
      body = (
        <PanelMessage
          icon="folder_off"
          title={t('chat.artifacts.viewer.notFoundTitle')}
          hint={t('chat.artifacts.viewer.notFoundHint')}
          action={{ label: closeLabel, onPress: onClose }}
        />
      );
      break;
    case 'error':
      body = (
        <PanelMessage
          icon="error"
          title={t('chat.artifacts.viewer.error')}
          action={{ label: t('chat.artifacts.actions.retry'), onPress: () => void detail.refetch() }}
        />
      );
      break;
    default:
      body = preview.url ? (
        variant === 'pdf' ? (
          // The browser's PDF viewer refuses sandboxed frames; the frame is
          // still cross-origin and sends no referrer.
          <iframe
            key={preview.url}
            src={preview.url}
            referrerPolicy="no-referrer"
            title={t('chat.artifacts.viewer.frameTitle', { title })}
            className="tabbio-artifact-panel-frame"
          />
        ) : (
          <iframe
            key={preview.url}
            src={preview.url}
            sandbox=""
            referrerPolicy="no-referrer"
            title={t('chat.artifacts.viewer.frameTitle', { title })}
            className="tabbio-artifact-panel-frame"
          />
        )
      ) : (
        <PanelMessage spinner title={t('chat.artifacts.viewer.loading')} />
      );
  }

  const closeButton = (
    <button
      type="button"
      className="tabbio-panel-icon-button"
      onClick={onClose}
      aria-label={closeLabel}
      title={closeLabel}
    >
      <MaterialSymbol name={closeMode === 'backToChat' && !docked ? 'arrow_back' : 'close'} size={20} />
    </button>
  );

  return (
    <section
      ref={panelRef}
      tabIndex={-1}
      className={`tabbio-artifact-panel is-${layout}`}
      aria-label={title}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && !downloadOpen && !shareOpen && !editing && view !== 'code') {
          event.stopPropagation();
          onClose();
        }
      }}
    >
      <header className="tabbio-artifact-panel-header">
        {docked ? null : closeButton}
        <MaterialSymbol
          name={KIND_ICONS[artifact?.kind ?? draft?.kind ?? 'document']}
          size={20}
          className="tabbio-artifact-panel-glyph"
        />
        <span className="tabbio-artifact-panel-title" dir="auto" title={title}>
          {title}
        </span>
        {draftState === 'building' ? (
          <span className="tabbio-artifact-panel-status" role="status">
            <SpinnerGlyph color="var(--text-secondary)" size={14} />
            {t('chat.artifacts.building.status')}
          </span>
        ) : agentEditing ? (
          <span className="tabbio-artifact-panel-status">
            <SpinnerGlyph color="var(--text-secondary)" size={14} />
            {t('chat.artifacts.building.editing')}
          </span>
        ) : editing ? (
          <SaveStatus
            state={saveState}
            onRetry={() => editorRef.current?.retry()}
            onReload={() => editorRef.current?.reload()}
          />
        ) : artifact?.status === 'ready' ? (
          <span className="tabbio-artifact-panel-saved" title={t('chat.artifacts.saved')}>
            <MaterialSymbol name="cloud_done" size={18} aria-hidden={false} />
          </span>
        ) : null}
        {live && draftState !== 'building' ? (
          <span
            className="tabbio-artifact-panel-status is-live"
            title={t('chat.artifacts.publish.liveChipLabel', { host: liveHost })}
          >
            <MaterialSymbol name="public" size={14} />
            {t('chat.artifacts.publish.liveChip')}
          </span>
        ) : null}
        <span className="tabbio-artifact-panel-spacer" />
        {editing ? (
          <button ref={doneButtonRef} type="button" className="tabbio-panel-primary is-small" onClick={() => void done()}>
            {t('chat.artifacts.edit.done')}
          </button>
        ) : canEdit ? (
          <button
            ref={editButtonRef}
            type="button"
            className="tabbio-panel-edit"
            onClick={enterEdit}
            aria-label={t('chat.artifacts.edit.editLabel', { title })}
          >
            <MaterialSymbol name="edit" size={18} />
            <span className="tabbio-panel-edit-label">{t('chat.artifacts.edit.edit')}</span>
          </button>
        ) : null}
        {artifact?.status === 'ready' && !editing ? (
          <>
            <button
              ref={shareRef}
              type="button"
              className="tabbio-panel-icon-button"
              onClick={() => setShareOpen((open) => !open)}
              aria-haspopup="menu"
              aria-expanded={shareOpen}
              aria-label={t('chat.artifacts.publish.share')}
              title={t('chat.artifacts.publish.share')}
            >
              <MaterialSymbol name="ios_share" size={20} />
            </button>
            {downloads.length ? (
              <button
                ref={downloadRef}
                type="button"
                className="tabbio-panel-download"
                onClick={() => setDownloadOpen((open) => !open)}
                aria-haspopup="menu"
                aria-expanded={downloadOpen}
                aria-busy={busy !== null || undefined}
              >
                {busy ? <SpinnerGlyph color="var(--text-primary)" size={16} /> : null}
                {t('chat.artifacts.actions.download')}
                <MaterialSymbol name="expand_more" size={18} />
              </button>
            ) : null}
          </>
        ) : null}
        {docked && onToggleExpanded ? (
          <button
            type="button"
            className="tabbio-panel-icon-button"
            onClick={onToggleExpanded}
            aria-label={expanded ? t('chat.artifacts.actions.collapse') : t('chat.artifacts.actions.expand')}
            title={expanded ? t('chat.artifacts.actions.collapse') : t('chat.artifacts.actions.expand')}
            aria-pressed={expanded}
          >
            <MaterialSymbol name={expanded ? 'close_fullscreen' : 'open_in_full'} size={20} />
          </button>
        ) : null}
        {docked ? closeButton : null}
      </header>

      <WebMenu
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        anchor={shareRef.current}
        placement="bottom-start"
        minWidth={220}
        style={{ borderRadius: 12 }}
      >
        <WebMenuItem
          icon="link"
          label={t('chat.artifacts.publish.copyPrivateLink')}
          onSelect={() => void copyLink()}
        />
        {live ? (
          <>
            <WebMenuItem
              icon="content_copy"
              label={t('chat.artifacts.publish.copySiteLink')}
              onSelect={() => void copySite()}
            />
            <WebMenuItem
              icon="open_in_new"
              label={t('chat.artifacts.publish.open')}
              onSelect={() => {
                if (site?.url) void openSite(site.url);
              }}
            />
            <WebMenuItem
              icon="tune"
              label={t('chat.artifacts.publish.manage')}
              onSelect={() => openPublish('manage')}
            />
          </>
        ) : publishing?.available && publishable ? (
          <WebMenuItem
            icon="public"
            label={t('chat.artifacts.publish.toWeb')}
            onSelect={() => openPublish('publish')}
          />
        ) : null}
      </WebMenu>

      <WebMenu
        open={downloadOpen}
        onClose={() => setDownloadOpen(false)}
        anchor={downloadRef.current}
        placement="bottom-start"
        minWidth={200}
        style={{ borderRadius: 12 }}
      >
        {downloads.map((option) => (
          <WebMenuItem
            key={option}
            icon={option === 'html' ? 'code' : option === 'pptx' ? 'slideshow' : option === 'image' ? 'image' : 'picture_as_pdf'}
            label={t(`chat.artifacts.download.${option}`)}
            onSelect={() => void download(option)}
          />
        ))}
      </WebMenu>

      {draftState === 'building' && draft ? (
        <ArtifactDraftPanel draft={draft} view={buildView} onViewChange={setBuildView} title={title} />
      ) : draftState === 'failed' ? (
        <div className="tabbio-artifact-panel-body">
          <PanelMessage icon="error" title={t(draftFailureKey(draft?.error?.code))} />
        </div>
      ) : null}

      {draftState === 'none' && state === 'ready' ? (
        <>
          <OlderVersionBanner
            control={versionControl}
            onRestored={(number) => toast.success(t('chat.artifacts.versions.restored', { number }))}
            onRestoreFailed={() => toast.error(t('chat.artifacts.versions.restoreFailed'))}
          />
          {tabs.length > 1 || versionControl.model.visible ? (
            <div className="tabbio-artifact-panel-tabs">
              {tabs.length > 1 ? (
                <div className="tabbio-panel-tablist" role="tablist">
                  {tabs.map((option) => (
                    <button
                      key={option}
                      type="button"
                      role="tab"
                      aria-selected={view === option}
                      className={view === option ? 'tabbio-panel-tab is-selected' : 'tabbio-panel-tab'}
                      onClick={() => {
                        if (option !== view && editing) void editorRef.current?.flush();
                        setView(option);
                      }}
                    >
                      {t(`chat.artifacts.viewer.${option}`)}
                    </button>
                  ))}
                </div>
              ) : null}
              <span className="tabbio-artifact-panel-spacer" />
              <VersionControl control={versionControl} />
            </div>
          ) : null}
        </>
      ) : null}

      {draftState === 'none' ? (
        editing && view === 'page' && artifact ? (
          <div className="tabbio-artifact-panel-body is-editing">
            {artifact.kind === 'page' ? (
              <PageEditor
                ref={editorRef}
                artifactId={artifactId}
                title={title}
                threadId={detail.data?.threadId ?? null}
                onSaveState={setSaveState}
                onAsked={askedCloses}
              />
            ) : (
              <DocumentEditor
                ref={editorRef}
                artifactId={artifactId}
                title={title}
                threadId={detail.data?.threadId ?? null}
                onSaveState={setSaveState}
                onAsked={askedCloses}
              />
            )}
          </div>
        ) : view === 'code' && artifact?.kind === 'page' && state === 'ready' ? (
          <div className={`tabbio-artifact-panel-body${agentEditing ? ' is-agent-editing' : ''}`}>
            {agentEditing ? <AgentEditingNote state={agentEditing} /> : null}
            <PageCodeEditor
              key={viewingId ?? `head:${head?.id ?? ''}`}
              artifactId={artifactId}
              title={title}
              threadId={detail.data?.threadId ?? null}
              versionId={viewingId}
              editable={editingAvailable && onLatest && !streaming}
              onAsked={askedCloses}
            />
          </div>
        ) : waitingForPdf && state === 'ready' ? (
          <div className="tabbio-artifact-panel-body">
            <PanelMessage spinner title={t('chat.artifacts.docEditor.preparingPdf')} />
          </div>
        ) : (
          <div className={`tabbio-artifact-panel-body${agentEditing ? ' is-agent-editing' : ''}`}>
            {agentEditing ? <AgentEditingNote state={agentEditing} /> : null}
            {body}
          </div>
        )
      ) : null}
    </section>
  );
}

/**
 * Over the dimmed file while the agent edits it: where the edit stands, in
 * a polite live region (announced when it starts and when it moves on).
 */
function AgentEditingNote({ state }: { state: NonNullable<AgentEditState> }) {
  const { t } = useTranslation('common');
  return (
    <div className="tabbio-panel-agent-edit" role="status" aria-live="polite">
      <span className="tabbio-panel-agent-edit-pill">
        <SpinnerGlyph color="var(--text-secondary)" size={16} />
        {t(state === 'checking' ? 'chat.artifacts.building.checking' : 'chat.artifacts.building.editing')}
      </span>
    </div>
  );
}

function PanelMessage({
  icon,
  spinner,
  title,
  hint,
  action,
}: {
  icon?: string;
  spinner?: boolean;
  title: string;
  hint?: string;
  action?: { label: string; onPress: () => void };
}) {
  return (
    <div className="tabbio-artifact-panel-message" role={spinner ? 'status' : undefined}>
      <span className="tabbio-artifact-panel-message-icon" aria-hidden>
        {spinner ? (
          <SpinnerGlyph color="var(--text-secondary)" size={22} />
        ) : (
          <MaterialSymbol name={icon ?? 'draft'} size={24} />
        )}
      </span>
      <span className="tabbio-artifact-panel-message-title">{title}</span>
      {hint ? <span className="tabbio-artifact-panel-message-hint">{hint}</span> : null}
      {action ? (
        <button type="button" className="tabbio-panel-primary" onClick={action.onPress}>
          {action.label}
        </button>
      ) : null}
    </div>
  );
}
